import type { Messages } from '../messages.js'

/**
 * Ukrainian. `хід` for flips, the word a Ukrainian board game uses for a move.
 *
 * Four plural forms on the same pattern as Russian, and `many` is the genitive plural that
 * covers 5 to 20 and everything ending in 5 to 9: `5 слів`, not `5 слова`.
 */
export const uk: Messages = {
  tag: 'uk',

  readingDictionary: 'Читаю словник…',
  noWordList: 'Немає списку слів для «{language}». Створіть його:  pnpm dictionary build',
  emptyWordList: 'Список слів для «{language}» порожній.',

  flips: 'ходи',
  score: 'рахунок',
  words: 'слова',
  round: 'раунд',
  ticksLeftLabel: 'Час, що лишився в цьому раунді',
  typeAWord: 'уведіть слово',
  tapPrompt: 'торкайтеся літер, щоб обрати або повернути, потім {action}',

  boardOfTiles: 'Дошка з {n} фішок',
  faceDown: 'сорочкою догори',
  wildCard: 'джокер',
  wildKey: 'будь-яка літера',
  letterReplaced: '{from} стало {to}',
  letterSwap: 'ЗАМІНА ЛІТЕР!',
  spentTile: 'використана фішка',
  hiddenWhilePaused: 'приховано під час паузи',
  letterInWord: '{letter}, {position}-та літера слова',

  completeWord: 'Завершити слово',

  completeShort: 'Готово',
  reset: 'Очистити',
  pause: 'Пауза',
  resume: 'Продовжити',
  newGame: 'Нова гра',
  paused: 'Призупинено',
  outOfFlips: 'Ходи скінчилися',
  finalResult: '{score} за {words} протягом {rounds}',
  playAgain: 'Зіграти ще раз',
  share: 'Поділитися',
  shareCopied: 'Скопійовано.',
  shareSelect: 'Скопіюйте це:',

  lettersSelect: 'літери обирають',
  keysWild: 'береться, коли ви вводите літеру, якої немає на дошці',
  clearsEvery: 'очищає всі обрані {letter}',
  undoLastLetter: 'скасувати останню літеру',
  noWordsYet: 'Поки що жодного слова.',

  wordRejected: '{word}  {reason}',
  reasonDuplicate: 'уже знайдено',
  reasonTooShort: 'закоротке',
  reasonNotAWord: 'це не слово',
  reasonAllFound: 'у вас уже є всі',
  noSuchLetterUp: 'немає відкритої {letter}',
  nothingUp: 'нічого не відкрито',
  letterHidden: 'літера сховалася',
  shuffled: 'перемішано',
  shuffledAndBilled: 'перемішано, зараховано {flips} невикористаних',
  tooFewLetters: 'Залишилося замало літер',

  gameLanguage: 'мова',
  interfaceLanguage: 'інтерфейс',
  dictionarySize: '{common} щоденних із {full} слів',
  filterLanguages: 'Пошук мови',
  noMatches: 'Нічого не знайдено',

  nerdMode: 'режим для знавців',
  rules: 'Правила',
  difficulty: 'складність',
  difficultyNames: { easy: 'легка', medium: 'середня', hard: 'важка', insane: 'божевільна' },
  pausedNoBoard: 'Ця гра не потрапить до таблиці лідерів.',
  about: 'Про гру',
  themeLabel: 'тема',
  themeNames: { traditional: 'традиційна', light: 'світла', contrast: 'високий контраст' },
  tiles: 'фішки (N)',
  secondsPerTick: 'секунди / такт',
  holdTicks: 'такти утримання',
  minWord: 'найкоротше слово',
  startingFlips: 'початкові ходи',
  wildChance: 'шанс джокера',
  replaceChance: 'шанс заміни літери',
  hideChance: 'шанс приховування літери',
  wordCompleteMode: 'коли слово завершено',
  wordCompleteNames: { shuffle: 'перемішати', spend: 'витратити', keep: 'лишити' },
  flipEconomy: 'повернення ходів',
  flipEconomyNames: {
    none: 'немає',
    perLetter: 'за літеру',
    fibonacci: 'фібоначі',
    overMinimum: 'понад мінімум',
  },
  repeatedLetterKey: 'клавіша повтореної літери',
  keySchemeNames: { cycle: 'коло', advance: 'уперед' },
  keySchemeHelp: {
    cycle:
      'А бере наступну невикористану А, а коли всі вони в слові, очищає їх. ' +
      'Shift+А теж їх очищає.',
    advance: 'А бере наступну невикористану А. Shift+А очищає зі слова всі А.',
  },

  whatThatMeans: 'Що це означає',
  factRound: 'раунд',
  factWholeBoardUp: 'уся дошка відкрита на',
  factRoundCosts: 'раунд коштує',
  factFlipsBuy: 'початкові ходи купують',
  factThisBoard: 'ця дошка',
  factBoardHadToAdmit: 'дошка мала допустити',
  ticksAndSeconds: '{ticks}, {seconds}',
  wordsLongest: '{words}, найдовше {longest}',
  wordsIncludingOneOf: '{words}, з них одне на {ceiling}',
  scorelessRounds: '{rounds} без очок',

  whatAWordPays: 'Скільки дає слово',
  columnLetters: 'літери',
  columnCost: 'ціна',
  columnPoints: 'очки',
  columnFlips: 'ходи',
  columnNet: 'різниця',

  canonicalRules: 'Звичайні правила для рівня {difficulty}.',
  customRules: 'Змінено від типових. Результати за власними правилами не потрапляють до таблиці.',
  applyAndStart: 'Застосувати й почати нову гру',
  changesNextGame: 'Зміни діятимуть із наступної гри.',
  presets: 'Типові набори:',

  start: 'Почати',
  restart: 'Спочатку',
  quit: 'Вийти',
  quitTitle: 'Вийти з цієї гри?',
  restartTitle: 'Почати цю гру спочатку?',
  restartConfirm: 'Спочатку',
  quitConfirm: 'Вийти',
  keepPlaying: 'Грати далі',
  personalBest: 'Ваші найкращі ігри',
  newPersonalBest: 'Новий особистий рекорд.',
  columnRank: '#',
  notRanked: 'Власні правила, тож ця гра не потрапляє до таблиці.',
  notRankedPaused: 'Годинник зупинився, тож ця гра не потрапляє до таблиці.',
  rankOfTotal: '{rank} з {total}',

  howToPlay: 'Як грати',

  backToGame: 'Назад до гри',
  welcomeTitle: 'Вітаємо в Blinkered',
  tutorialSkip: 'Пропустити',
  tutorialNext: 'Далі',
  tutorialBack: 'Назад',
  tutorialStart: 'Почати грати',
  tutorialHideAgain: 'Більше не показувати',
  tutorialProgress: '{n} з {total}',
  tutorialSkipTitle: 'Пропустити знайомство?',
  tutGoalTitle: 'Blinkered — це перегони',
  tutGoalBody:
    'Ви починаєте гру з певною кількістю ходів, і кожна відкрита фішка коштує один хід. Коли ходи скінчаться, гру завершено. Слова приносять ходи, і що довше слово, то більше ходів ви отримуєте.',
  tutSpentBody:
    'Літери, які ви використали в слові, лишаються закритими до наступного раунду. Лише заміна літери змінює, які літери є на дошці.',
  tutChangeTitle: 'Літери можуть змінюватися',
  tutSameLetters:
    'Помітили слово, але не встигли його зіграти? Ті самі літери повернуться в наступному раунді, перемішані на нових місцях.',
  tutPickLetters: 'Торкайтеся потрібних літер по черзі, щоб скласти слово.',
  tutMoreTurn: 'Фішки відкриваються, поки ви думаєте, тож краща літера ще може прийти.',
  tutTapBack:
    'Узяли не ту літеру? Торкніться її ще раз, і вона повернеться. Будь-якої, не лише останньої.',
  tutComplete: 'Коли слово готове, натисніть Готово.',
  htBoardTitle: 'Дошка',
  htBoardBody: 'Фішки відкриваються по одній, у випадковому порядку. З відкритих складають слова.',
  htWordsTitle: 'Слова',
  htWordsBody: 'Складіть слово з відкритих фішок, уводячи або клацаючи літери по черзі.',
  htFlipsTitle: 'Ходи',
  htFlipsBody:
    'Кожна відкрита фішка коштує хід. Завершене слово повертає ходи, а довші слова повертають більше. Коли ходи скінчаться, гру завершено.',
  htRoundTitle: 'Раунд',
  htRoundBody:
    'Коли відкриється остання фішка раунду, вся дошка на мить завмирає. Потім фішки перевертають і перемішують, і починається новий раунд.',
  htLanguagesTitle: 'Мови',
  htLanguagesBody:
    'Їх {n}. Кожну дошку можна розв’язати словами, які люди справді вживають. Рідкісне слово теж дає очки, якщо словник його знає.',
  htKeysTitle: 'Клавіатура',
  htWildTitle: 'Джокери',
  htWildBody:
    'Іноді замість літери з’являється джокер. Джокер зараховують як будь-яку літеру, що утворює правильне слово. Уже знайдене слово не рахується.',
  htHideTitle: 'Літери, що ховаються',
  htHideBody:
    'На складніших рівнях відкрита літера може сама перевернутися і повернутися згодом у тому ж раунді. Таймер отримує за це додаткову позначку, червону. Уже обрані літери не перевертаються.',
  tutHideBody:
    'На складніших рівнях відкрита літера може сама перевернутися і повернутися згодом у тому ж раунді. Уже обрані літери не перевертаються.',
  htSwapTitle: 'Літери, що змінюються',
  htSwapBody:
    'Іноді між раундами одну літеру замінюють іншою. Ви побачите, яка зникла і яка з’явилася.',
  htLevelsTitle: 'Рівні',
  htLevelEasy:
    'Ті самі дванадцять літер усю гру, тож їх можна вивчити й тримати список слів у голові. Фішки відкриваються повільно, а вся дошка лишається на очах досить довго, щоб завершити вибір.',
  htLevelMedium:
    'Час від часу літера змінюється, тож важче пам’ятати слова, відкладені на потім. Менше часу дивитися і менше думати.',
  htLevelHard:
    'Слова з трьох літер перестають рахуватися, а літера змінюється приблизно щодругий раунд. Дошка щойно відкрилася — і вже перемішується.',
  htLevelInsane:
    'Усе водночас і на повній швидкості. Дошка перемішується майже одразу після останнього ходу.',
  htTouchTitle: 'Сенсорний екран',
  htTouchBody:
    'Торкніться відкритої фішки, щоб узяти її літеру. Торкніться взятої літери, щоб повернути її. Готово й Очистити — під дошкою.',

  accountTitle: 'Ваш обліковий запис',
  menuProfile: 'Мій профіль',
  menuGames: 'Мої ігри',
  menuPublicPage: 'Моя публічна сторінка',
  menuSignedInAs: 'Ви увійшли як',
  signIn: 'Увійти',
  signOut: 'Вийти',
  signInTitle: 'Увійдіть або зареєструйтеся',
  signInLead: 'Обліковий запис зберігає ваші ігри, на всіх пристроях.',
  signInKeepGame: 'Увійдіть, щоб зберегти цю гру, і всі наступні.',
  continueWithApple: 'Продовжити з Apple',
  continueWithGoogle: 'Продовжити з Google',
  signInOr: 'або',
  emailLabel: 'Ел. пошта',
  codeLabel: 'Код',
  emailMeACode: 'Надішліть мені код',
  working: 'Хвилинку…',
  sendNewCode: 'Надіслати новий код',
  codeSent: 'Перевірте пошту. Код діє 10 хвилин.',
  notNow: 'Не зараз',
  badEmail: 'Це не схоже на адресу електронної пошти.',
  badCode: 'Код не підійшов. Коди діють 10 хвилин і лише один раз.',
  serverBusy: 'Не вдалося зв’язатися з сервером. Спробуйте за хвилину.',
  serverDown: 'Не вдалося зв’язатися з сервером.',
  offline: 'Офлайн',
  leaderboardTitle: 'Таблиця лідерів',
  leaderboardEmpty: 'У цій таблиці ще немає ігор. Стань першим.',
  leaderboardWouldBe: 'Де була б ця гра',
  leaderboardThisGame: 'Ця гра',
  leaderboardRanksHere: 'Де ця гра стоїть',
  leaderboardOutside: 'Поза першою п’ятіркою.',
  tutAccountTitle: 'Збережи свої результати',
  tutAccountBody:
    'Акаунт зберігає твої ігри, додає результати до таблиці лідерів і зберігає твій профіль.',
  ssoCancelled: 'Вхід скасовано.',
  ssoExpired: 'Це тривало надто довго. Увійдіть ще раз.',
  ssoNoUsername: 'Не вдалося створити обліковий запис до кінця. Спробуйте за хвилину.',
  ssoFailed: 'Не вийшло. Спробуйте ще раз або скористайтеся поштою.',
  tabProfile: 'Профіль',
  countryAny: 'Не скажу',
  findCountry: 'Пошук країн',
  usernameLabel: 'Ім’я користувача',
  bioLabel: 'Про себе',
  countryLabel: 'Країна',
  bioHint: 'Без посилань. {left}/{max}',
  save: 'Зберегти',
  saving: 'Зберігаємо…',
  saved: 'Збережено.',
  nameTaken: 'Таке ім’я вже зайняте.',
  nameTooShort: 'Трохи довше, будь ласка.',
  nameTooLong: 'Задовге.',
  nameBadCharacters: 'Лише літери, цифри, дефіси та підкреслення.',
  nameBadEdges: 'Має починатися й закінчуватися літерою або цифрою.',
  nameMixedScripts: 'Оберіть одну абетку й тримайтеся її.',
  nameReserved: 'Схоже на ім’я, яке видаємо ми самі. Оберіть інше.',
  nameUnusable: 'Це ім’я використати не можна.',
  bioTooLong: 'У полі «Про себе» вміщується не більше {max} символів.',
  bioHasLink: 'Посилання в полі «Про себе» заборонені.',
  bioHasControl: 'Тут є символи, яких це поле не допускає.',
  bioUnusable: 'Цей текст використати не можна.',
  gamesHeading: 'Ігри',
  gamesEmpty: 'Тут поки порожньо. Ігри, зіграні після входу, з’являться тут.',
  gamesEmptyShort: 'Тут поки порожньо.',
  gamesLoading: 'Читаємо ваші ігри…',
  theirGamesLoading: 'Читаємо їхні ігри…',
  profileLoading: 'Читаємо профіль…',
  playerNotFound: 'Тут немає нікого з таким іменем.',
  columnWhen: 'Коли',
  columnRounds: 'раунду',
  columnGame: 'Гра',
  gameLoading: 'Читаємо гру…',
  gameUnreadable: 'Цю гру не вдалося прочитати.',
  gameNotRanked: 'Зіграна за зміненими правилами, тому не йде в залік.',
  detailNotKept: 'Слова й дошки цієї гри більше не зберігаються.',
  openingBoard: 'Початкова дошка',
  roundByRound: 'Раунд за раундом',
  roundNothing: 'нічого не знайдено',
  changeReplaced: '{letter} замінено в клітинці {slot}',
  changeWildOn: '{letter} джокер у клітинці {slot}',
  changeWildOff: '{letter} джокер прибрано з клітинки {slot}',
  keepThisGame: 'Зберегти цю гру',
  savedToYourGames: 'Збережено у ваших іграх.',

  reportThis: 'Поскаржитися',
  reportHeading: 'Поскаржитися на це',
  reportTheName: 'Ім’я',
  reportTheBio: 'Опис',
  reportTheScore: 'Рахунок',
  reportWhy: 'Що з цим не так? Необов’язково.',
  reportSend: 'Надіслати скаргу',
  reportSent: 'Дякуємо. Хтось це перегляне.',
  reportSignIn: 'Увійдіть, щоб на щось поскаржитися.',
  reportGone: 'Цього більше немає.',
  reportCancel: 'Скасувати',

  deleteAccount: 'Видалити мій акаунт',
  deleteAccountWhat:
    'Це назавжди прибере твій профіль і всі збережені ігри. Скасувати буде не можна.',
  deleteAccountAsk: 'Надішліть мені код',
  deleteConfirm: 'Видалити назавжди',
  deleteGone: 'Твого акаунта більше немає. Дякуємо за гру.',
  deleteNoAddress:
    'На цьому акаунті немає підтвердженої адреси, тож видалення не можна перевірити. Напиши нам, і ми зробимо це вручну.',

  rulesetFacts: 'Слова від {letters} · нова фішка кожні {seconds}',
  plurals: {
    words: { one: '{n} слово', few: '{n} слова', many: '{n} слів', other: '{n} слова' },
    rounds: { one: '{n} раунд', few: '{n} раунди', many: '{n} раундів', other: '{n} раунду' },
    flips: { one: '{n} хід', few: '{n} ходи', many: '{n} ходів', other: '{n} ходу' },
    ticks: { one: '{n} такт', few: '{n} такти', many: '{n} тактів', other: '{n} такту' },
    points: { one: '{n} очко', few: '{n} очка', many: '{n} очок', other: '{n} очка' },
    minLetters: { one: '{n} літери', few: '{n} літер', many: '{n} літер', other: '{n} літери' },
    seconds: { one: '{n} секунди', few: '{n} секунди', many: '{n} секунди', other: '{n} секунди' },
  },
}
